import { Button, Indicator, Tooltip } from "@mantine/core";
import { IconBell } from "@tabler/icons-react";
import { setShowNotificationsInRedux } from "../global/globalSlice";
import { useAppDispatch, useAppSelector } from "../hooks";
import { useTranslation } from "../i18n";
import { useGetNotificationsQuery } from "./notificationRTK";

const NOTIFICATIONS_LAST_OPEN_KEY = "notificationsLastOpen";
const NOTIFICATIONS_SEEN_KEYS_KEY = "notificationsSeenKeys";
const NOTIFICATIONS_POLL_INTERVAL_MS = 60_000;

const getValidTime = (date: Date | string | null | undefined) => {
  if (date == null) {
    return null;
  }

  const time = new Date(date).getTime();
  return Number.isNaN(time) ? null : time;
};

const getNotificationKey = (notification: {
  _id?: string;
  createdAt?: Date | string;
  itemDBid?: string;
  text?: string;
}) => notification._id ?? `${notification.createdAt ?? ""}-${notification.itemDBid ?? ""}-${notification.text ?? ""}`;

const getSeenNotificationKeys = () => {
  const storedValue = localStorage.getItem(NOTIFICATIONS_SEEN_KEYS_KEY);

  if (storedValue == null) {
    return null;
  }

  try {
    const parsedValue = JSON.parse(storedValue);
    return Array.isArray(parsedValue) ? new Set(parsedValue.filter((value) => typeof value === "string")) : null;
  } catch {
    return null;
  }
};

export const NotificationButton = () => {
  const t = useTranslation();
  const user = useAppSelector((state) => state.globalReducer.user);
  const notifications = user ? user.notifications : [];

  const dispatch = useAppDispatch();
  useGetNotificationsQuery(undefined, {
    pollingInterval: NOTIFICATIONS_POLL_INTERVAL_MS,
    skip: user == null,
  });

  const newNotificationsCount = (() => {
    const seenNotificationKeys = getSeenNotificationKeys();

    if (seenNotificationKeys != null) {
      return notifications.filter((notification) => !seenNotificationKeys.has(getNotificationKey(notification))).length;
    }

    const lastOpenedDate = localStorage.getItem(NOTIFICATIONS_LAST_OPEN_KEY);
    const lastOpenedTime = getValidTime(lastOpenedDate);

    if (lastOpenedTime == null) {
      return notifications.filter((notification) => notification.markedAsRead?.value === false).length;
    }

    return notifications.filter((notification) => {
      const createdAtTime = getValidTime(notification.createdAt);
      return createdAtTime != null && createdAtTime > lastOpenedTime;
    }).length;
  })();

  const newNotificationsLabel = newNotificationsCount > 99 ? "99+" : newNotificationsCount;

  const showNotification = useAppSelector((state) => state.globalReducer.showNotifications);
  const hasNewNotifications = newNotificationsCount > 0;

  return (
    <Tooltip label={t("notifications.show")} withArrow>
      <Indicator
        disabled={!hasNewNotifications}
        inline
        label={newNotificationsLabel}
        size={16}
        offset={10}
        position="top-end"
        color="red.6"
        styles={{
          indicator: {
            fontSize: 10,
            fontWeight: 700,
            lineHeight: 1,
            minWidth: 16,
            paddingInline: 4,
          },
        }}
      >
        <Button
          variant="subtle"
          c={hasNewNotifications ? "red.5" : "white"}
          size="compact-xl"
          onClick={() => {
            const next = !showNotification;
            localStorage.setItem(NOTIFICATIONS_LAST_OPEN_KEY, new Date().toISOString());
            localStorage.setItem(
              NOTIFICATIONS_SEEN_KEYS_KEY,
              JSON.stringify(notifications.map((notification) => getNotificationKey(notification))),
            );
            dispatch(setShowNotificationsInRedux(next));
          }}
        >
          <IconBell size={20} />
        </Button>
      </Indicator>
    </Tooltip>
  );
};
